import React from 'react';
import { Box, theme, Stack, HStack, Button, Divider, Center, Text } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { HiOutlineDeviceMobile, HiMenuAlt4 } from 'react-icons/hi';
import { useStoreState } from '../../../../models/hooks';
import { JsonPreview } from '../jsonPreview/JsonPreview';
import MobilePreviewView from './mobilePreview/MobilePreviewView';
import SectionPreview from './listViewPreview/SectionPreview';
import { isSectionSchema } from '../utils';
import ElementPreview from './listViewPreview/ElementPreview';

function PreviewSidebarComponent() {
  const isHireFormJsonApiViewerEnabled =
    useStoreState((s) => s.app.accounts?.configuration?.hireFormJsonApiViewerEnabled) ?? false;
  const previewModes = {
    device: { icon: <HiOutlineDeviceMobile />, label: 'deviceView' },
    list: { icon: <HiMenuAlt4 />, label: isHireFormJsonApiViewerEnabled ? 'jsonView' : 'listView' },
  } as const;

  const { t } = useTranslation('hrFormTemplate');
  const schema = useStoreState((state) => state.formBuilder.formSchema);
  const language = useStoreState((state) => state.formBuilder.currentLanguage);

  const [previewMode, setPreviewMode] = React.useState<keyof typeof previewModes>('device');

  return (
    <Box minHeight={0} height="100%" background={theme.colors.white} paddingY={theme.space[6]} paddingX={theme.space[4]}>
      <Stack minHeight={0} height="100%">
        <HStack justify="space-between">
          <Text fontSize="lg" fontWeight="bold">
            {t('formBuilder.preview.title')}
          </Text>
          <HStack justify="flex-end">
            <Button
              size="sm"
              colorScheme="gray"
              onClick={() => setPreviewMode((prev) => (prev === 'device' ? 'list' : 'device'))}
            >
              {t(`formBuilder.preview.${previewModes[previewMode === 'device' ? 'list' : 'device'].label}`)}
            </Button>
          </HStack>
        </HStack>

        {previewMode === 'list' &&
          (isHireFormJsonApiViewerEnabled ? (
            <JsonPreview />
          ) : (
            <Stack
              sx={{
                '& ol, & ul': {
                  margin: 'revert',
                  padding: 'revert',
                },
              }}
              divider={<Divider />}
            >
              {schema.map((item) =>
                isSectionSchema(item) ? (
                  <SectionPreview key={item.id} schema={item} language={language} />
                ) : (
                  <ElementPreview key={item.id} schema={item} language={language} />
                ),
              )}
            </Stack>
          ))}
        {previewMode === 'device' && (
          <Center flexGrow={1}>
            <MobilePreviewView hideBusinessLogo />
          </Center>
        )}
      </Stack>
    </Box>
  );
}

export const PreviewSidebar = React.memo(PreviewSidebarComponent, () => true);
